import { CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import type { ScrapingProgress } from '@/Domain/store/upload.store';

type Props = {
  successMessage: string | null;
  hasPendingFiles: boolean;
  hasErrorFiles: boolean;
  scrapingProgress?: ScrapingProgress;
  pendingUrl?: string;
  // Optional "Added X of N — M failed: …" string attached by the backend to
  // the terminal META_ARTICLE_DELETED emit on partial-failure Multiple Pages
  // submits. Renders as the success headline + a muted sub-line listing the
  // failed URLs (truncated at 3 by the backend) so users don't lose
  // visibility into which links didn't make it.
  partialFailureMessage?: string;
  // Derived from subscription's `can_add_article` in the parent. When the
  // user hits their plan's articles_limit mid-scrape, surface a small amber
  // hint inside the in-flight pill so they know the scrape will stop early,
  // rather than only finding out when the terminal capped-success view fires.
  kbAtLimit?: boolean;
};

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

// Pin a value to its highest-seen-so-far per hook instance — defends against
// stale lower counts a sibling Firecrawl webhook handler can emit under a burst.
const useMonotonicMax = (value: number): number => {
  const maxRef = useRef(value);
  if (value > maxRef.current) maxRef.current = value;
  return maxRef.current;
};

/**
 * Tween a numeric counter toward `target` over ~600ms on increase so bursts
 * of socket updates (Firecrawl fires many crawl.page events within a few
 * hundred ms) feel like continuous progress instead of a snap to the end.
 * Decreases jump instantly.
 */
const useAnimatedCounter = (target: number, durationMs = 600): number => {
  const [displayed, setDisplayed] = useState(target);
  // Mirror `displayed` in a ref so the tween effect can read the latest value
  // without depending on it — depending on `displayed` would re-run the effect
  // on every frame and reset the tween's `from` baseline, killing the ease-out.
  const displayedRef = useRef(target);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const from = displayedRef.current;
    if (target < from) {
      displayedRef.current = target;
      setDisplayed(target);
      return;
    }
    if (target === from) return;

    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(progress);
      const next = Math.round(from + (target - from) * eased);
      displayedRef.current = next;
      setDisplayed(next);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        frameRef.current = null;
      }
    };

    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs]);

  return displayed;
};

/**
 * Detect that the scrape has stalled — page events stopped flowing but we
 * haven't received the terminal completion event. After `staleAfterMs` of
 * silence we render "Finalizing…". 30s threshold avoids false-firing on
 * Firecrawl's normal 5-15s batching cadence.
 */
const useStallDetector = (
  signalKey: string | number,
  active: boolean,
  staleAfterMs = 30000,
): boolean => {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!active) {
      setStalled(false);
      return;
    }
    setStalled(false);
    const id = window.setTimeout(() => setStalled(true), staleAfterMs);
    return () => window.clearTimeout(id);
  }, [signalKey, active, staleAfterMs]);

  return stalled;
};

/**
 * Throttle a value so it updates at most once per `intervalMs`. Used for the
 * currentUrl sub-line: without this the URL can flicker a dozen times in
 * <500ms during a Firecrawl burst, which is faster than the human eye can
 * read. Trailing edge always gets the final value.
 */
const useThrottledValue = <T,>(value: T, intervalMs: number): T => {
  const [throttled, setThrottled] = useState(value);
  const lastUpdateRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const pendingRef = useRef(value);

  useEffect(() => {
    pendingRef.current = value;
    const now = Date.now();
    const sinceLast = now - lastUpdateRef.current;
    if (sinceLast >= intervalMs) {
      setThrottled(value);
      lastUpdateRef.current = now;
      return;
    }
    if (timerRef.current !== null) return;
    timerRef.current = window.setTimeout(() => {
      setThrottled(pendingRef.current);
      lastUpdateRef.current = Date.now();
      timerRef.current = null;
    }, intervalMs - sinceLast);
  }, [value, intervalMs]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  return throttled;
};

const friendlyUrl = (raw: string): string => raw.replace(/^https?:\/\//i, '').replace(/\/$/, '');

export const StatusMessage = ({
  successMessage,
  hasPendingFiles,
  hasErrorFiles,
  scrapingProgress,
  pendingUrl,
  partialFailureMessage,
  kbAtLimit = false,
}: Props) => {
  const { t } = useTranslation(['knowledge_base']);
  const { capture } = usePostHogAnalytics();
  const { getConfig } = useSubscription();

  // Clamp both to monotonic max — the store only guards visitedItems.
  const visitedTarget = useMonotonicMax(scrapingProgress?.visitedItems ?? 0);
  const totalTarget = useMonotonicMax(scrapingProgress?.totalItems ?? 0);
  const cappedAtLimit = scrapingProgress?.cappedAtLimit ?? false;
  const visitedDisplay = useAnimatedCounter(visitedTarget);
  // Prefer the live current URL from the backend; fall back to the pending
  // URL the user submitted so the widget shows context the instant a file
  // is queued, before any webhook arrives.
  const liveUrl = scrapingProgress?.currentUrl ?? pendingUrl ?? '';
  const throttledUrl = useThrottledValue(liveUrl, 500);

  const hasAnyProgressSignal = Boolean(scrapingProgress) || hasPendingFiles;
  const isComplete = !hasPendingFiles && !hasErrorFiles;
  // Gated on totalTarget > 1: single-page scrapes emit SCRAPING_PING(1/1)
  // and then wait on finalize_meta_article_background, which can exceed the
  // stall threshold. "Finalizing…" on a one-page job is noise.
  const isFinalizing = useStallDetector(
    `${visitedTarget}|${scrapingProgress?.currentUrl ?? ''}`,
    hasPendingFiles && !hasErrorFiles && visitedTarget > 0 && totalTarget > 1,
  );
  // Bar tracks the animated count, not the raw target, so width and headline
  // grow in lockstep — snap to 100% on completion or finalize to avoid mid-fill.
  const ratio =
    isComplete || isFinalizing
      ? 1
      : totalTarget > 0
        ? Math.min(1, visitedDisplay / totalTarget)
        : 0;

  const wasCappedSuccess = useRef(false);
  useEffect(() => {
    const isCappedSuccess = isComplete && cappedAtLimit;
    if (isCappedSuccess && !wasCappedSuccess.current) {
      capture(PostHogEventEnum.KbFullPillViewed, {
        articles_used: getConfig('articles_used') as number | undefined,
        articles_limit: getConfig('articles_limit') as number | null | undefined,
        visited_target: visitedTarget,
      });
    }
    wasCappedSuccess.current = isCappedSuccess;
  }, [isComplete, cappedAtLimit, visitedTarget, capture, getConfig]);

  let tone: 'pending' | 'success' | 'warning' | 'error' = 'pending';
  let headline = '';
  let sub: string | null = null;

  if (hasErrorFiles) {
    tone = 'error';
    headline = t('modals.status_modal.status_message.adding_knowledge_failed');
  } else if (isComplete && partialFailureMessage) {
    // Mixed success — the backend attached a "Added 3 of 5 — 2 failed: …"
    // summary to the terminal META_ARTICLE_DELETED emit because at least
    // one link failed. Preserves the per-URL failure visibility that the
    // old N-file model surfaced in the Failed tab.
    tone = 'success';
    const splitAt = partialFailureMessage.indexOf(' — ');
    headline = splitAt === -1 ? partialFailureMessage : partialFailureMessage.slice(0, splitAt);
    sub = splitAt === -1 ? null : partialFailureMessage.slice(splitAt + 3);
  } else if (isComplete && cappedAtLimit) {
    // Scrape stopped because the scenario hit its plan's articles_limit.
    // Render in a warning tone (amber) rather than success (green): the
    // user hit a wall, "completed" would be misleading. The count line
    // moves to the sub-line so they can still see how many pages were added.
    tone = 'warning';
    headline = t('modals.status_modal.status_message.kb_full');
    sub =
      visitedTarget > 0
        ? t('modals.status_modal.status_message.added_pages', { count: visitedTarget })
        : t('modals.status_modal.status_message.kb_full_sub');
  } else if (isComplete && visitedTarget > 0) {
    // Page-count headline wins over the generic `successMessage` prop
    // (which `useArticleFileStatus` sets to "Adding Knowledge successful!"
    // on the knowledge_base_updated socket event) — once we've actually
    // scraped pages, the count is the most useful confirmation.
    tone = 'success';
    headline = t('modals.status_modal.status_message.added_pages', { count: visitedTarget });
  } else if (successMessage) {
    tone = 'success';
    headline = successMessage;
  } else if (isComplete) {
    tone = 'success';
    headline = t('modals.status_modal.status_message.adding_knowledge_successful');
  } else if (isFinalizing) {
    headline = t('modals.status_modal.status_message.finalizing');
    sub = null;
  } else if (visitedTarget > 0) {
    headline = t('modals.status_modal.status_message.scraping_pages', { count: visitedDisplay });
    sub = throttledUrl ? friendlyUrl(throttledUrl) : null;
  } else if (throttledUrl) {
    // Earliest pending state — file is queued, backend hasn't sent a page
    // event yet. Show the URL the user submitted so the widget feels
    // immediately responsive.
    headline = t('modals.status_modal.status_message.starting_scrape');
    sub = friendlyUrl(throttledUrl);
  } else {
    headline = t('modals.status_modal.status_message.adding_knowledge_in_progress');
  }

  if (tone === 'error') {
    return (
      <div className="flex items-center gap-2 text-sm rounded-lg py-2.5 px-3.5 font-medium text-destructive bg-destructive-muted border border-destructive/20">
        <AlertCircle className="w-4 h-4 flex-shrink-0 text-destructive" strokeWidth={2.5} />
        <span className="truncate">{headline}</span>
      </div>
    );
  }

  if (tone === 'success') {
    // Clean success: no bar, just a confident check + line. When we carry a
    // partial-failure sub-line, stack it below the headline in a muted color.
    return (
      <div className="flex flex-col gap-2 text-sm rounded-lg py-2.5 px-3.5 font-medium text-success bg-success-muted border border-success/20">
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-success" strokeWidth={2.5} />
          <span className="truncate min-w-0">{headline}</span>
        </div>
        {sub && (
          <div className="pl-6 min-w-0 overflow-hidden">
            <p className="text-xs font-normal text-success/80 truncate" title={sub}>
              {sub}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (tone === 'warning') {
    // Capped at the plan's articles_limit, so use a warning tone so the
    // user immediately sees this isn't a clean success. UpgradePlanButton
    // sits on the headline row, right-aligned, so the upgrade path is one
    // click away without taking a separate row.
    return (
      <div
        className="flex flex-col gap-2 text-sm rounded-lg py-2.5 px-3.5 font-medium text-warning bg-warning-muted border border-warning/20"
        data-testid="kb-full-pill"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-warning" strokeWidth={2.5} />
          <span className="truncate min-w-0 flex-1">{headline}</span>
          <div className="flex-shrink-0" data-testid="kb-full-upgrade-cta">
            <UpgradePlanButton size="xs" source="kb_full_pill" />
          </div>
        </div>
        {sub && (
          <div className="pl-6 min-w-0 overflow-hidden">
            <p className="text-xs font-normal text-warning/80 truncate" title={sub}>
              {sub}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Pending: spinner / pulse + headline + URL sub-line + bar. Bar renders
  // from the first pending frame (even before totalItems is known) at 0% so
  // the widget's layout stays stable, otherwise it would slip in a few
  // seconds later when the first Firecrawl webhook landed.
  const showBar = hasAnyProgressSignal;
  const percent = Math.round(ratio * 100);

  return (
    <div className="flex flex-col gap-2 text-sm rounded-lg py-2.5 px-3.5 font-medium text-foreground bg-muted border border-border">
      <div className="flex items-center gap-2 min-w-0">
        {isFinalizing ? (
          <div className="w-2 h-2 flex-shrink-0 bg-foreground rounded-full animate-pulse mx-1" />
        ) : (
          <Spinner className="w-4 h-4 flex-shrink-0" />
        )}
        <span className="truncate min-w-0">{headline}</span>
      </div>
      {sub && (
        <div className="pl-6 min-w-0 overflow-hidden">
          <p className="text-xs font-normal text-muted-foreground truncate" title={sub}>
            {sub}
          </p>
        </div>
      )}
      {showBar && (
        <div
          className="h-1 w-full bg-muted-foreground/15 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-foreground rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {/* Mid-scrape hint when the user has hit articles_limit. The terminal
          capped-success branch above handles the post-scrape state; this
          covers the gap where the crawl is still running or finalizing and
          the user would otherwise see no indication that the scrape is
          stopping early. Driven from the live `subscription` data
          (can_add_article === false), kept fresh by useKbUsageSocket. */}
      {kbAtLimit && (
        <div
          className="flex items-center gap-1.5 text-xs font-medium text-warning"
          data-testid="status-message-at-limit-hint"
        >
          <AlertTriangle className="w-3 h-3 flex-shrink-0 text-warning" />
          <span>{t('kb_limit_reached')}</span>
        </div>
      )}
    </div>
  );
};
